/*

 This file implements an AppToastr which can invoke succes/fail toastrs from anywhere in the app that uses this
 ToastrContext.  You can use this with the toastr-middleware by adding a toastr context to a fetch call.

 Example usage:

 import { ToastrContext } from '@purecarslabs/signal-components/baseline/AppToastr';

 const Example = () => {
   const handleClick = (showToastr) => {
     showToastr({
       text: 'some text',
       severity: 'success', // or 'warning', 'error'
     });

     // or, you can use the toastr middleware to automatically get toastrs on fetch, see below
     fetchUsingToastrMiddleware({ someParameter: 'foo', showToastr });
   };

   return (
     <ToastrContext.Consumer>
       {({ showToastr }) => (
         <Button onClick={handleClick(showToastr)}>
       )}
     </ToastrContext.Consumer>
   );
 };

 the above example manually generates a toastr with 'showToastr', but if you are using a fetch then the fetcher should
 be implemented in a way that supports the toastr-middleware so you can get success and error toastrs automatically

 const fetchUsingToastrMiddleware = ({ someParameter, showToastr }) => {
   // the second parameter to fetch() should be a middlewareOptions object.  You can get toastrs if you provide
   // showToastr and fetchContext text
   fetch(`v1/test/${someParametr}`)({
     showToastr, // you must pass this function thru to middleware which can't see React contexts.
     fetchContext: 'Lead resend', // text to put in the toastr to give context to a success or error
   });
 };

*/

import React, { createContext, memo, useState } from 'react'
import PropTypes from 'prop-types'
import Typography from '@purecarslabs/signal-components/Typography'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import renderIf from '@purecarslabs/signal-utils/renderIf'
import isTest from '@purecarslabs/signal-utils/env/isTest'
import { useStyles, Alert, Snackbar } from './styles'

const TOASTR_AUTO_HIDE_MSEC = 10000

export const ToastrContext = createContext({
  showToastr: () => {}
})

const AppToastr = ({ children }) => {
  const classes = useStyles()
  const [toastrState, setToastrState] = useState({ toastrOpen: false })
  const { toastrOpen, toastrSeverity, toastrText } = toastrState

  const showToastr = ({ severity, text }) => {
    setToastrState({
      toastrOpen: true,
      toastrSeverity: severity,
      toastrText: text
    })
  }

  const hideToastr = () => {
    setToastrState({
      ...toastrState,
      toastrOpen: false
    })
  }

  return (
    <ToastrContext.Provider value={{ showToastr }}>
      <Snackbar
        data-testid="toastr-snackbar"
        autoHideDuration={TOASTR_AUTO_HIDE_MSEC}
        open={toastrOpen}
        onClose={hideToastr}
      >
        <Alert
          classes={{
            icon: classes.toastrAlertIcon
          }}
          onClose={hideToastr}
          severity={toastrSeverity}
        >
          {renderIf(!isTest() || toastrOpen, () => (
            <Typography
              color="inverse"
              muiClasses={{
                root: classes.toastrTypographyRoot
              }}
              variant="h6"
            >
              {toastrText}
            </Typography>
          ))}
        </Alert>
      </Snackbar>
      {children}
    </ToastrContext.Provider>
  )
}

AppToastr.propTypes = {
  children: PropTypes.node
}

export default memo(withCommonSnapshotsOnly(AppToastr))
